import React, { useState, useRef, useEffect, ReactNode } from "react";
import ReactDOM from "react-dom";
import "./Dropdown.less";
import { Button } from "../Button/Button";
import { Link } from "react-router-dom";
import { useUserRegistry } from "../../../context/RegistryContexts/RegistryUserContext";

export interface DropdownItem {
   id: string;
   name: string | ReactNode;
   value?: string;
   onClick?: (item: DropdownItem) => void;
   disabled?: boolean;
   buttonType?: "button" | "link";
   link?: string;
   customItemClassName?: string;
   customButton?: boolean;
   type?: "separator";
}
interface DropdownInterface {
   items?: Array<DropdownItem>;
   style?: React.CSSProperties;
   setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
   customItemStyle?: React.CSSProperties;
   isOpen: boolean;
   className?: string;
   noDataInfo?: ReactNode | string;
   onOptionClick?: (option: DropdownItem) => void;
   customZIndex?: number;
   checkForModalRoot?: boolean;
   ignoreRef?: React.RefObject<any>;
}

export const Dropdown: React.FC<DropdownInterface> = ({
   items,
   style,
   setIsOpen,
   customItemStyle,
   isOpen,
   className,
   noDataInfo = "No data",
   onOptionClick,
   customZIndex,
   checkForModalRoot = true,
   ignoreRef,
}) => {
   const [dropdownHeight, setDropdownHeight] = useState<number>();
   const [dropdownWidth, setDropdownWidth] = useState<number>();
   const dropdownRoot = document.getElementById("dropdown-root");
   const modalRoot = document.getElementById("modal-root");

   const dropdownRef = useRef<HTMLDivElement>(null);
   const dropdownPortalRef = useRef<HTMLDivElement>(null);

   const { isNavGridEnabled } = useUserRegistry();

   // this useEffect manage closing dropdown by clicking anywhere, but not on dropdown
   useEffect(() => {
      const handleOutsideClick = (event: MouseEvent) => {
         if (dropdownPortalRef.current && event.target instanceof HTMLElement) {
            const clickedInsideDropdown = dropdownPortalRef.current.contains(event.target);
            const clickedOnTrigger =
               !!ignoreRef?.current && ignoreRef.current.contains(event.target);
            if (clickedOnTrigger) return;

            const shouldClose = !clickedInsideDropdown;
            if (checkForModalRoot) {
               if (modalRoot?.hasChildNodes()) return;
               setIsOpen(!shouldClose);
            } else {
               setIsOpen(!shouldClose);
            }
         }
      };

      document.addEventListener("mousedown", handleOutsideClick);
      return () => document.removeEventListener("mousedown", handleOutsideClick);
   }, [modalRoot, setIsOpen, checkForModalRoot]);

   const handleItemClick = (listObj: DropdownItem) => {
      if (listObj.onClick) {
         setIsOpen(false);
         listObj.onClick(listObj);
      } else if (onOptionClick) {
         setIsOpen(false);
         return onOptionClick(listObj);
      } else {
         return listObj;
      }
   };

   const renderDropdownButtons = (list: DropdownItem[] | React.ReactNode | React.ReactElement) => {
      setTimeout(() => {
         const actualDropdownElement = document.getElementsByClassName("bp2-dropdown-body")[0] as
            | HTMLElement
            | undefined;
         setDropdownHeight(actualDropdownElement?.clientHeight || 0);
         setDropdownWidth(actualDropdownElement?.clientWidth || 0);
      }, 0);

      if (React.isValidElement(list) && list.type === "ul") {
         const ul = list as React.ReactElement<React.HTMLAttributes<HTMLUListElement>>;
         return (
            <div className="items-container">
               {React.cloneElement<React.HTMLAttributes<HTMLUListElement>>(ul)}
            </div>
         );
      }

      if (Array.isArray(list) && list.some((element) => React.isValidElement(element))) {
         const nodes = list as React.ReactNode[];
         const hasLi = nodes.some(
            (node) => React.isValidElement(node) && (node.type as any) === "li",
         );
         if (hasLi) {
            return (
               <div className="items-container">
                  <ul>
                     {nodes.map((node, i) =>
                        React.isValidElement(node)
                           ? React.cloneElement(node as any, {
                                key: (node as any).key ?? `li-${i}`,
                             })
                           : null,
                     )}
                  </ul>
               </div>
            );
         }
      }

      const arr = Array.isArray(list) ? (list as DropdownItem[]) : [];
      return (
         <div className="items-container">
            {arr.length > 0
               ? arr.map((listObj) => {
                    if (listObj?.buttonType === "link") {
                       return listObj?.disabled ? (
                          <span
                             key={listObj.id?.toString()}
                             className={`${listObj?.customItemClassName} dropdown-button-item`}
                             style={{
                                ...customItemStyle,
                                cursor: "not-allowed",
                             }}
                          >
                             {listObj?.name}
                          </span>
                       ) : (
                          <Link
                             style={customItemStyle}
                             key={listObj.id?.toString()}
                             to={listObj?.link ? listObj?.link : ""}
                             target={"_blank"}
                             className={`${listObj?.customItemClassName} dropdown-button-item`}
                          >
                             {listObj?.name}
                          </Link>
                       );
                    } else if (listObj?.type === "separator") {
                       return (
                          <div key={`sep-${listObj?.id}`} className="dropdown-button-separator" />
                       );
                    } else if (listObj?.customButton) {
                       return (
                          <div key={String(listObj.id)} onClick={(e) => e.stopPropagation()}>
                             {listObj.name}
                          </div>
                       );
                    } else {
                       return (
                          <Button
                             datatestid="dropdown-option-button"
                             className={`${listObj?.customItemClassName} dropdown-button-item`}
                             disabled={listObj?.disabled}
                             style={customItemStyle}
                             onClick={(e) => {
                                e.stopPropagation();
                                handleItemClick(listObj);
                             }}
                             key={listObj?.id?.toString()}
                          >
                             {listObj?.name}
                          </Button>
                       );
                    }
                 })
               : noDataInfo}
         </div>
      );
   };

   const inScreenPositionY = () => {
      const parentRect = dropdownRef?.current?.parentElement?.getBoundingClientRect();
      const parentRectBottom = parentRect?.bottom || 0;
      const parentRectTop = parentRect?.top || 0;

      let yPosition = parentRectBottom;

      if (dropdownHeight) {
         if (parentRectBottom + dropdownHeight > window.innerHeight) {
            yPosition = parentRectTop - dropdownHeight;
         }
         if (yPosition < 0) {
            yPosition = 0;
         }
      }

      return yPosition;
   };

   const inScreenPositionX = () => {
      const parentRect = dropdownRef?.current?.parentElement?.getBoundingClientRect();
      const parentRectLeft = parentRect?.left || 0;
      const parentRectRight = parentRect?.right || 0;

      let xPosition = parentRectLeft;

      if (dropdownWidth) {
         if (parentRectLeft + dropdownWidth > window.innerWidth) {
            xPosition = parentRectRight - dropdownWidth;
         }
         if (xPosition < 0) {
            xPosition = 0;
         }
      }

      return xPosition;
   };

   useEffect(() => {
      const dropdownRoot = document.getElementById("dropdown-root");

      if (dropdownRoot) {
         dropdownRoot.setAttribute("data-nav-grid-enabled", isNavGridEnabled || "0");
      }
   }, [isNavGridEnabled]);

   return (
      <div className={"bp2-dropdown " + "main-dropdown"} ref={dropdownRef} tabIndex={0}>
         {isOpen &&
            dropdownRef?.current?.parentElement?.getBoundingClientRect() &&
            ReactDOM.createPortal(
               <div
                  data-testid="dropdown-main-div"
                  role="dropdown-main-div"
                  className={`${className} bp2-dropdown-body position-top`}
                  ref={dropdownPortalRef}
                  style={{
                     position: "absolute",
                     left: inScreenPositionX(),
                     top: inScreenPositionY(),
                     transform: "translateY(0px)",
                     zIndex: customZIndex || 1,
                  }}
               >
                  <div>{items && renderDropdownButtons(items)}</div>
               </div>,
               //@ts-ignore
               dropdownRoot,
            )}
      </div>
   );
};
export default Dropdown;
